import styled, { keyframes } from 'styled-components'

// Animations
export const pulse = keyframes`
  0%, 100% { 
    opacity: 0.5; 
    transform: scale(1); 
  }
  50% { 
    opacity: 0.8; 
    transform: scale(1.1); 
  }
`

export const float = keyframes`
  0%, 100% { 
    transform: translateY(0px); 
  }
  50% { 
    transform: translateY(-20px); 
  }
`

export const shimmer = keyframes`
  0% {
    transform: translateX(-100%);
  }
  100% {
    transform: translateX(100%);
  }
`

export const fadeIn = keyframes`
  from { 
    opacity: 0; 
    transform: translateX(-50%) translateY(20px); 
  }
  to { 
    opacity: 1; 
    transform: translateX(-50%) translateY(0); 
  }
`

// Main Container
export const Container = styled.div`
  min-height: 100vh;
  background: linear-gradient(135deg, #0f0f23 0%, #1a1a3e 50%, #2d1b69 100%);
  position: relative;
  overflow: hidden;
`

// Animated Background Elements
export const BackgroundOrb = styled.div`
  position: fixed;
  border-radius: 50%;
  animation: ${pulse} ${props => props.duration || '8s'} ease-in-out infinite ${props => props.reverse ? 'reverse' : ''};
  
  &.orb-1 {
    top: 10%;
    left: 10%;
    width: 300px;
    height: 300px;
    background: radial-gradient(circle, rgba(124, 58, 237, 0.1) 0%, transparent 70%);
  }
  
  &.orb-2 {
    top: 60%;
    right: 5%;
    width: 200px;
    height: 200px;
    background: radial-gradient(circle, rgba(236, 72, 153, 0.1) 0%, transparent 70%);
    animation-duration: 6s;
    animation-direction: reverse;
  }
`

// Content Wrapper
export const ContentWrapper = styled.div`
  position: relative;
  z-index: 1;
  padding: 0 16px;
`

// Hero Section
export const HeroSection = styled.div`
  background: linear-gradient(135deg, rgba(139, 69, 19, 0.1) 0%, rgba(30, 58, 138, 0.1) 100%);
  border-radius: 32px;
  margin: 20px auto;
  max-width: 1200px;
  overflow: hidden;
  backdrop-filter: blur(20px);
  border: 1px solid rgba(255, 255, 255, 0.1);
  box-shadow: 0 32px 64px rgba(0, 0, 0, 0.3);
`

export const HeroContent = styled.div`
  padding: 40px;
  background: linear-gradient(135deg, rgba(99, 102, 241, 0.1) 0%, rgba(139, 69, 19, 0.1) 100%);
`

export const HeroGrid = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 40px;
  align-items: center;
  
  @media (max-width: 768px) {
    flex-direction: column;
    text-align: center;
  }
`

export const HeroMain = styled.div`
  flex: 1 1 300px;
  min-width: 300px;
`

export const HeroAside = styled.div`
  flex: 0 0 280px;
  
  @media (max-width: 768px) {
    flex: 1 1 auto;
  }
`

export const EventHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 20px;
  margin-bottom: 24px;
  flex-wrap: wrap;
`

export const EventIcon = styled.div`
  width: 120px;
  height: 120px;
  border-radius: 24px;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-weight: bold;
  box-shadow: 0 16px 32px rgba(0, 0, 0, 0.2);
`

export const EventInfo = styled.div`
  flex: 1;
  min-width: 200px;
`

export const DateInfo = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 12px;
`

export const DateText = styled.div`
  color: #10b981;
  font-size: 16px;
  font-weight: 500;
`

export const EventTitle = styled.h1`
  color: white;
  font-weight: 700;
  margin-bottom: 12px;
  font-size: 2.5rem;
  line-height: 1.2;
  background: linear-gradient(135deg, #fff 0%, #e5e7eb 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  margin: 0 0 12px 0;
  
  @media (max-width: 768px) {
    font-size: 2rem;
  }
`

export const VenueInfo = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`

export const VenueLink = styled.a`
  color: #f59e0b;
  text-decoration: none;
  font-size: 14px;
  
  &:hover {
    text-decoration: underline;
  }
`

// Quick Stats
export const StatsContainer = styled.div`
  display: flex;
  gap: 16px;
  flex-wrap: wrap;
`

export const StatBadge = styled.div`
  background: ${props => props.color === 'green' 
    ? 'rgba(16, 185, 129, 0.1)' 
    : 'rgba(245, 158, 11, 0.1)'};
  border: 1px solid ${props => props.color === 'green' 
    ? 'rgba(16, 185, 129, 0.3)' 
    : 'rgba(245, 158, 11, 0.3)'};
  border-radius: 16px;
  padding: 12px 20px;
  display: flex;
  align-items: center;
  gap: 8px;
  
  span {
    color: ${props => props.color === 'green' ? '#10b981' : '#f59e0b'};
    font-weight: 500;
  }
`

// Share Card
export const ShareCard = styled.div`
  background: rgba(255, 255, 255, 0.05);
  border-radius: 20px;
  padding: 24px;
  text-align: center;
  backdrop-filter: blur(10px);
  border: 1px solid rgba(255, 255, 255, 0.1);
`

export const ShareHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  margin-bottom: 16px;
`

export const ShareTitle = styled.div`
  color: #8b5cf6;
  font-weight: 600;
`

export const QRContainer = styled.div`
  width: ${props => props.size || 120}px;
  height: ${props => props.size || 120}px;
  background: white;
  padding: 8px;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 12px;
  color: #333;
  text-align: center;
  margin: 0 auto;
`

// Details Section
export const DetailsSection = styled.div`
  margin-top: 32px;
  border-top: 1px solid rgba(255, 255, 255, 0.1);
  padding-top: 24px;
`

export const ExpandButton = styled.button`
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  border: none;
  border-radius: 12px;
  padding: 12px 24px;
  color: white;
  font-weight: 500;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 8px;
  margin: 0 auto;
  transition: all 0.3s ease;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.2);
  
  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 6px 16px rgba(0, 0, 0, 0.3);
  }
`

export const CollapsibleContent = styled.div`
  max-height: ${props => props.isOpen ? '2000px' : '0'};
  overflow: hidden;
  transition: max-height 0.3s ease-in-out;
`

export const DetailsGrid = styled.div`
  margin-top: 24px;
  display: flex;
  flex-wrap: wrap;
  gap: 20px;
`

export const DetailCard = styled.div`
  flex: 1 1 300px;
  background: rgba(255, 255, 255, 0.05);
  border-radius: 16px;
  padding: 20px;
  border: 1px solid rgba(255, 255, 255, 0.1);
`

export const DetailTitle = styled.h3`
  color: ${props => props.color || '#10b981'};
  margin-bottom: 12px;
  font-weight: 600;
  margin: 0 0 12px 0;
`

export const DetailContent = styled.div`
  color: rgba(255, 255, 255, 0.8);
`

// Participants Section
export const ParticipantsSection = styled.div`
  max-width: 1200px;
  margin: 40px auto;
  padding: 0 20px;
`

export const ParticipantsHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 12px;
  margin-bottom: 32px;
`

export const ParticipantsIcon = styled.div`
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  border-radius: 50%;
  width: 40px;
  height: 40px;
  display: flex;
  align-items: center;
  justify-content: center;
`

export const ParticipantsTitle = styled.h2`
  color: white;
  font-weight: 700;
  font-size: 1.5rem;
  background: linear-gradient(135deg, #fff 0%, #e5e7eb 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  margin: 0;
`

export const ParticipantsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 16px;
  margin-top: 16px;
  
  @media (max-width: 768px) {
    grid-template-columns: repeat(2, 1fr);
  }
  
  @media (max-width: 480px) {
    grid-template-columns: 1fr;
  }
`;

// Participant Card
export const ParticipantCard = styled.div`
  cursor: ${props => props.anonymous ? 'default' : 'pointer'};
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  border-radius: 20px;
  padding: 16px;
  position: relative;
  overflow: hidden;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  transform: translateY(0);
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.1);
  min-height: 140px;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  
  &:hover {
    transform: ${props => props.anonymous ? 'translateY(0)' : 'translateY(-8px)'};
    box-shadow: ${props => props.anonymous 
      ? '0 8px 32px rgba(0, 0, 0, 0.1)' 
      : '0 20px 40px rgba(0, 0, 0, 0.2)'};
  }
`

export const ParticipantBg = styled.div`
  position: absolute;
  top: -50%;
  right: -50%;
  width: 100px;
  height: 100px;
  background: rgba(255, 255, 255, 0.1);
  border-radius: 50%;
  animation: ${float} 6s ease-in-out infinite;
`

export const StatusBadge = styled.div`
  position: absolute;
  top: 12px;
  right: 12px;
  background: rgba(34, 197, 94, 0.9);
  border-radius: 50%;
  width: 24px;
  height: 24px;
  display: flex;
  align-items: center;
  justify-content: center;
  backdrop-filter: blur(10px);
`

export const ParticipantContent = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  position: relative;
  z-index: 2;
`

export const ParticipantAvatar = styled.div`
  width: 60px;
  height: 60px;
  border-radius: 50%;
  background: ${props => props.anonymous 
    ? 'linear-gradient(135deg, #a855f7 0%, #ec4899 100%)'
    : 'linear-gradient(135deg, #06b6d4 0%, #3b82f6 100%)'};
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 12px;
  border: 3px solid rgba(255, 255, 255, 0.2);
  font-size: 20px;
  font-weight: bold;
  color: white;
`

export const ParticipantName = styled.div`
  color: white;
  font-size: 14px;
  font-weight: 600;
  margin-bottom: 4px;
  text-align: center;
`

export const ParticipantAlias = styled.div`
  color: rgba(255, 255, 255, 0.8);
  font-size: 12px;
  text-align: center;
`

// Floating Action Button
export const FloatingButton = styled.div`
  position: fixed;
  bottom: 32px;
  right: 32px;
  z-index: 1000;
`

export const JoinButton = styled.button`
  background: linear-gradient(135deg, #10b981 0%, #059669 100%);
  border: none;
  border-radius: 24px;
  padding: 16px 32px;
  color: white;
  font-weight: 600;
  font-size: 16px;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 12px;
  box-shadow: 0 12px 24px rgba(16, 185, 129, 0.3);
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  transform: translateY(0);
  
  &:hover {
    transform: translateY(-4px);
    box-shadow: 0 16px 32px rgba(16, 185, 129, 0.4);
  }
`

export const FullButton = styled.div`
  background: linear-gradient(135deg, #ef4444 0%, #dc2626 100%);
  border-radius: 24px;
  padding: 16px 32px;
  color: white;
  font-weight: 600;
  font-size: 16px;
  display: flex;
  align-items: center;
  gap: 12px;
  box-shadow: 0 12px 24px rgba(239, 68, 68, 0.3);
`

// Modal
export const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.8);
  display: ${props => props.isOpen ? 'flex' : 'none'};
  align-items: center;
  justify-content: center;
  z-index: 9999;
  padding: 20px;
`

export const ModalContent = styled.div`
  background: linear-gradient(135deg, #1f2937 0%, #374151 100%);
  border-radius: 24px;
  padding: 40px;
  max-width: 500px;
  width: 90%;
  box-shadow: 0 32px 64px rgba(0, 0, 0, 0.5);
  border: 1px solid rgba(255, 255, 255, 0.1);
  position: relative;
`

export const ModalCloseButton = styled.button`
  position: absolute;
  top: 16px;
  right: 16px;
  background: none;
  border: none;
  color: white;
  cursor: pointer;
  padding: 8px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  
  &:hover {
    background: rgba(255, 255, 255, 0.1);
  }
`

export const ModalTitle = styled.h2`
  color: white;
  margin-bottom: 20px;
  text-align: center;
  font-weight: 700;
  margin: 0 0 20px 0;
`

export const ModalDescription = styled.div`
  color: rgba(255, 255, 255, 0.8);
  text-align: center;
  margin-bottom: 30px;
`

export const ModalPlaceholder = styled.div`
  background: rgba(255, 255, 255, 0.05);
  border-radius: 12px;
  padding: 20px;
  text-align: center;
  color: rgba(255, 255, 255, 0.6);
`

// Snackbar
export const SnackbarContainer = styled.div`
  position: fixed;
  bottom: 20px;
  left: 50%;
  transform: translateX(-50%);
  background: #333;
  color: white;
  padding: 12px 24px;
  border-radius: 8px;
  z-index: 10000;
  animation: ${fadeIn} 0.3s ease-in-out;
  display: ${props => props.isOpen ? 'block' : 'none'};
`

export const AvatarImage = styled.img`
 width: 100%;
 height: 100%;
 border-radius: 50%;
 object-fit: cover;
 position: relative;
 z-index: 1;
`